<template>
    <div class="main">
        <div v-if="!isEditing">
            <div class="content">
                <div v-if="projects.length > 0" class="projects">
                    <ul class="project-list">
                        <li v-for="project in projects" :key="project.id" :class="{ 'selected': selectedProject?.id === project.id }" @click="selectProject(project)">
                            <div class="project">
                                {{ project.project_name }}
                                <div class="img-text">
                                    <img class="img" src="../assets/img.png" alt="">
                                    <label class="label">{{ project.nr_images }}</label>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
                <div v-else>
                    <label class="lblNoProjects">NO PROJECTS FOUND</label>
                </div>
                <div v-if="projects.length > 0" class="options">
                    <img class="option" :class="{'disabled':!selectedProject || !isAdmin}" src="../assets/recycle-bin.png" @click="deleteProject">
                    <img class="option" :class="{ 'disabled': !selectedProject || !isAdmin }" src="../assets/editproject.png" title="Edit Project" @click="editSelectedProject"/>
                </div>
            </div>
            <div class="buttons">
                <Button  :class="{'btnNoProjects': projects.length === 0, 'disabled':!selectedProject}" color="#d1d1d1" @click="openProject">OPEN</Button>
                <Button :class="{ 'btnNoProjects': projects.length === 0, 'disabled': !selectedProject || !(isAdmin || isReviewer) }" color="#d1d1d1" @click="openProjectForReview">REVIEW</Button>
                <Button  color="red" @click="Cancel">CANCEL</Button>
            </div>
        </div>
        <NewProject v-if="isEditing && editingProject" :initialProject="editingProject" :isEditMode="true" @cancelNewProject="cancelEdit"/>
    </div>
</template>

<script lang="ts">
    import { defineComponent, ref, onMounted  } from "vue"
    import Button from "../components/Button.vue"
    import { useStore } from "vuex";
    import { useRouter } from "vue-router";
    import ProjectService from "../services/Project";
    import { Project } from "../helpers/Interfaces";
    import NewProject from './NewProject.vue';
    import { supabase } from "@/supabase";

    export default defineComponent ({
        components: {
            Button,
            NewProject
        },
        setup(props, { emit }){
            const store = useStore();
            const router = useRouter();
            const projects = ref<Project[]>([]);
            const selectedProject = ref<Project | null>(null);
            const editingProject = ref<Project | null>(null);
            const isEditing = ref(false);
            const isAdmin = ref(false);
            const isReviewer = ref(false);

            async function getProjects() {
                try {
                    // Loads projects from supabase
                    projects.value = await ProjectService.getProjectsForCurrentUser()
                } catch (err) {
                    console.error("Error loading projects:", err)
                    projects.value = []
                }
            }

            function selectProject(project:Project) {
                if (selectedProject.value === project) {
                    selectedProject.value = null;
                } else {
                    selectedProject.value = project;
                }
            }

            function openProject(project:Project) {
                if (!selectedProject.value) return;
            
                // Define o projeto atual no Vuex
                store.commit("setCurrentProject", selectedProject.value);

                // Navega para o editor com `projectId`
                router.push({ name: "editor" });
            }

            function openProjectForReview() {
                if (!selectedProject.value) return;
                store.commit("setCurrentProject", selectedProject.value);
                router.push({ name: "review" });
            }

            async function deleteProject() {
                if (!selectedProject.value) {
                    alert("No project was selected.")
                    return
                }
                if (
                    confirm(`Delete Project "${selectedProject.value.project_name}"?`)
                ) {
                    try {
                        await ProjectService.deleteProject(selectedProject.value)

                        await getProjects()
                        selectedProject.value = null
                    } catch (err) {
                        console.error("Error deleting project:", err)
                        alert("Failed to delete project. Check console.")
                    }
                }
            }

            function editSelectedProject() {
                if (!selectedProject.value) return;
                editingProject.value = { ...selectedProject.value };
                isEditing.value = true;
            }

            async function cancelEdit() {
                editingProject.value = null;
                isEditing.value = false;

                await getProjects();

                if (selectedProject.value) {
                    const refreshed = projects.value.find((p) => p.id === selectedProject.value!.id);
                    selectedProject.value = refreshed ?? null;
                }                
            }

            function Cancel() {
                emit('cancelOpenProject');
            }
            
            onMounted(async() => {
                const loadProjects = async () => {
                    await getProjects();
                };

                isAdmin.value = await ProjectService.isCurrentUserAdmin();

                //const { data: { user } } = await supabase.auth.getUser();
                const { data: { session } } = await supabase.auth.getSession();
                const user = session?.user ?? null;                
                if (user) {
                    const { data: profile } = await supabase
                    .from('profiles')
                    .select('isreviewer')
                    .eq('user_id', user.id)
                    .maybeSingle();
                    isReviewer.value = !!profile?.isreviewer;
                }

                loadProjects();
            });

            return { projects, selectProject, openProject, deleteProject, selectedProject, Cancel, editSelectedProject,
                     cancelEdit, editingProject, isEditing, isAdmin, isReviewer, openProjectForReview };
        }
    });

</script>

<style lang="scss" scoped>
    .main {
        display: flex;
        flex-direction: column;
    }

    .content {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
    }

    .options {
        margin-top: 20px;
        margin-left: 20px;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-right: 20px; /* Add some space between the image column and the project list */
    }

    .disabled {
        opacity: 20%;
        pointer-events: none;
    }

    .option {
        padding: 4px;
        width: 32px;
        height: 32px;
        margin-bottom: 10px;
        border: 2px solid #767676;
        border-radius: 4px;
    }

    .option:hover {
        border: 2px solid #4883d4;
    }

    

    .buttons {
        display: flex;
        flex-direction: row;
        justify-content: space-evenly;
        align-items: center;
        margin-top: 20px;
        width: 370px;
    }

    .lblNoProjects {
        color: rgb(182, 182, 182);
        font-family: sans-serif;
    }

    .btnNoProjects {
        opacity: 30%;
        pointer-events: none;
    }

    .project {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    .img-text {
        position: relative;
        display: inline-block;
    }

    .img {
        display: block;
        width: 20px;
        height: 20px;
    }

    .label {
        position: absolute;
        top: 50%;
        left: -20%;
        top: 80%;
        transform: translate(-50%, -50%);
        color: white;
        font-size: 18px;
        font-weight: bold;
        background: #151515;
    }

    ul {
        border-left: 2px solid #767676;
        border-right: 2px solid #767676;
        padding-top: 10px;
        padding-bottom: 10px;
        margin-left: 80px;
    }

    li {
        width: 300px;
        height: 15px;
        display: flex;
        align-items: center;
        margin: -2px;
        cursor: pointer;
        padding: 10px;
        background: #151515;
        position: relative;
        color: white;
        border-radius: 2px;
        transition: background 0.3s ease;
    }

    li {
        left: -5%;
        border-top: 2px solid #767676;
        border-bottom: 2px solid #767676;
        border-left: 2px solid #151515;
        border-right: 2px solid #151515;
    }

    ul li:hover {
        background-color: #2a2a2a;

        .label {
            background: #2a2a2a;
        }        
    }

    ul li.selected {
        background-color: #4a4a4a;
        border-left: 2px solid #4883d4;
        border-right: 2px solid #4883d4;

        .label {
            background: #4a4a4a;
        }
    }

</style>